import React, { useState, useEffect } from 'react';
import { FormControl, Autocomplete, TextField, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import FieldNames from '../../../defaults/FieldNames.json';
import UserHelperService from '../../../services/UserHelperService';
import { useSelector } from 'react-redux';
import ErrorService from '../../../services/ErrorService';
import ProjectService from '../../../services/ProjectService';
import { getProjectNameDateIdentifier } from '../../../utils/ProjectHelper';

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red"
    },
    "&&:after": {
      borderBottom: "2px solid red"
    }
  },
});

const useNotSavedStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid gold"
    },
    "&&:after": {
      borderBottom: "2px solid gold"
    }
  },
});

const theme = createTheme({
  overrides: {
    
  },
});

export default function DetailsAutoComplete({userInfo, field, parentValue, selectionValue, setSelectionValue, userRoleInfo, onFieldChange, detail, isRequired, parentType, selectionFundingAgency, selectionFundingRegion}) {
  const [options, setOptions] = useState([]);
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const nifcToken = useSelector((state) => state.configuration.nifcToken);

  const treatmentValidationUnsavedValues = useSelector((state) => { return state.treatmentValidation });
  const activityValidationUnsavedValues = useSelector((state) => { return state.activityValidation });
  const actualsTreatmentValidationUnsavedValues = useSelector((state) => { return state.actualsTreatmentValidation });
  const actualsActivityValidationUnsavedValues = useSelector((state) => { return state.actualsActivityValidation });
  const notsavedclasses = useNotSavedStyles();
  const classes = useStyles();
  const [menuLoading, setMenuLoading] = useState(false);
  const [allProjects, setAllProjects] = useState([]);

  const [savedSelectionValue,setSavedSelectionValue] = useState("");

  useEffect(() => {
    if(detail && detail.attributes && detail.attributes[field])
    {
      setSavedSelectionValue(detail.attributes[field]);
    }
  }, [])

  useEffect(() => {
    if(detail && detail.attributes && detail.attributes[field]){
      setSelectionValue(detail.attributes[field])
      setOptions([detail.attributes[field], "N/A"])
    } else {
      setOptions(["N/A"])
    }
  }, [detail?.attributes[field]]);

  const showNotSavedClasses = (field) => {
    var fieldState = false;
    if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.treatment)
    {
      // Estimated Treatment
      if(treatmentValidationUnsavedValues === null || !field || field.length === 0)
      {
        return false;
      }
      fieldState = treatmentValidationUnsavedValues[field];
      return fieldState;
    }
    else if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment)
    {
      // Actual Treatment
      if(actualsTreatmentValidationUnsavedValues === null || !field || field.length === 0)
      {
        return false;
      }
      fieldState = actualsTreatmentValidationUnsavedValues[field];
      return fieldState;
    }
    else if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.activity)
    {
      // Estimated Activity
      if(activityValidationUnsavedValues === null || !field || field.length === 0)
      {
        return false;
      }
      fieldState = activityValidationUnsavedValues[field];
      return fieldState;
    }
    else if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity)
    {
      // Actual Activity
      if(actualsActivityValidationUnsavedValues === null || !field || field.length === 0)
      {
        return false;
      }
      fieldState = actualsActivityValidationUnsavedValues[field];
      return fieldState;
    }
    else
    {
      return false;
    }
  }

  const inputValueInvalid = (field) => {
    if(detail && detail.attributes){
      if(
        // Existence check
        (!detail.attributes[field] && !selectionValue) || 
        // N/A check for derived fields
        (FieldNames && (field === FieldNames.featureFields.department ||
        field === FieldNames.featureFields.agency ||
        field === FieldNames.featureFields.region ||
        field === FieldNames.featureFields.unit) && 
        ((detail.attributes[field] && detail.attributes[field].toLowerCase() === "n/a" && 
        (!detail.attributes[FieldNames.featureFields.locationDepartmentNaValid] === FieldNames.yesNo.yes)))) 
        // Worth noting the department n/a check covers all cases for now but may not in the future
        ){
        return true;
      } else {
        // Valid input
        return false;
      }
    } else {
      return true;
    }
  }

  const getMenuOptions = () => {
    return new Promise(function(resolve) {
      if(field === FieldNames.featureFields.agency){
        UserHelperService.getAllLocationAgencies().then((resp) => {
          if(resp && resp.length > 0 && detail && detail.attributes){
            if(detail.attributes[field])
            {
              setSelectionValue(detail.attributes[field]);
            }
            let returnedAgencies = resp.map(x => x[FieldNames.featureFields.agency]);

            // Override location agency "BIA" to be "BIA, Tribal" per FUELS-563
            returnedAgencies[returnedAgencies.map((x, i) => [i, x]).filter(
              x => x[1] === FieldNames.agencies.bia)[0][0]] = FieldNames.agencyOverrides.biaTribal;

            let finalAgencies = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                  detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
            finalAgencies = [...finalAgencies, ...returnedAgencies];
            let unique = [...new Set(finalAgencies)];
            resolve(unique);
          } else {
            if(detail.attributes[field])
            {
              setSelectionValue(detail.attributes[field]);
            }
            let finalAgencies = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                  detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
            resolve(finalAgencies);
          }
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          resolve([]);
        });
      } else if(field === FieldNames.featureFields.fundingDepartment){
        let departments = [];
        userRoleInfo.forEach(item => {
          if(item.Department && departments && !departments.includes(item.Department)){
            departments.push(item.Department);
          }
        })
        if(departments && departments.length > 0){
          resolve(departments);
        } else {
          resolve([FieldNames.defaults.department]);
        }
      } else if(field === FieldNames.featureFields.fundingAgency){
        let agencies = [];
        userRoleInfo.forEach(item => {
          if(item.Agency && agencies && !agencies.includes(item.Agency)){
            agencies.push(item.Agency);
          }
        })
        if(agencies && agencies.length > 0){
          resolve(agencies);
        } else {
          //User has Department level permissions only so they get all Agencies
          if(parentValue){
            UserHelperService.getAgencyByDepartment(parentValue).then((resp) => {
              if(resp && resp.length > 0){
                const returnedAgencies = resp.map(x => x.Agency);
                resolve(returnedAgencies);
              } else {
                resolve([]);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          }
        }
      } else if(field === FieldNames.featureFields.fundingRegion){
        let regions = [];
        userRoleInfo.forEach(item => {
          if(item.Region && regions && !regions.includes(item.Region)){
            regions.push(item.Region);
          }
        })
        if(regions && regions.length > 0){
          resolve(regions);
        } else {
          if(parentValue){
            UserHelperService.getRegionsByAgency(parentValue).then((resp) => {
              if(resp && resp.length > 0){
                const returnedRegions = resp.map(x => x.Region);
                resolve(returnedRegions);
              } else {
                resolve([]);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          } else {
            resolve([]);
          }
        }
      } else if(field === FieldNames.featureFields.fundingUnit){
        let units = [];
        userRoleInfo.forEach(item => {
          if(item.Region === parentValue){
            if(item.Unit && units && !units.includes(item.Unit)){
              units.push(item.Unit);
            }
          }
        })
        if(units && units.length > 0){
          resolve(units);
        } else {
          if(parentValue || selectionFundingRegion){
            var regionVal = parentValue ? parentValue : selectionFundingRegion;
            regionVal = [regionVal];
            if(detail && detail.attributes && 
              (detail.attributes[FieldNames.featureFields.fundingAgency] || selectionFundingAgency))
            {
              var agencyVal = detail.attributes[FieldNames.featureFields.fundingAgency] ? 
                                detail.attributes[FieldNames.featureFields.fundingAgency] : selectionFundingAgency;
              UserHelperService.getUnitsByRegionsAgency(regionVal, agencyVal).then((resp) => {
                if(resp && resp.length > 0){
                  const returnedUnits = resp.map(x => x.Unit);
                  resolve(returnedUnits);
                } else {
                  resolve([]);
                }
              }).catch(error => {
                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                resolve([]);
              });
            }
            else
            {
              UserHelperService.getUnitsByRegions(regionVal).then((resp) => {
                if(resp && resp.length > 0){
                  const returnedUnits = resp.map(x => x.Unit);
                  resolve(returnedUnits);
                } else {
                  resolve([]);
                }
              }).catch(error => {
                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                resolve([]);
              });
            }
          } else {
            resolve([]);
          }
        }
      } else if(field === FieldNames.featureFields.fundingSubUnit){
        let subUnits = [];
        userRoleInfo.forEach(item => {
          if(item.Unit === parentValue){
            if(item.SubUnit && subUnits && !subUnits.includes(item.SubUnit)){
              subUnits.push(item.SubUnit);
            }
          }
        })
        if(subUnits && subUnits.length > 0){
          resolve(subUnits);
        } else {
          if(parentValue){
            UserHelperService.getSubUnitsByUnits([parentValue]).then((resp) => {
              if(resp && resp.length > 0){
                const returnedSubUnits = resp.map(x => x.SubUnit);
                resolve(returnedSubUnits);
              } else {
                resolve([]);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          } else {
            resolve([]);
          }
        }
      } else if(field === FieldNames.featureFields.tribeName){
        UserHelperService.getAllTribes().then((resp) => {
          if(resp && resp.length > 0){
            const returnedTribes = resp.map(x => x[FieldNames.units.tribeFullName] ? x[FieldNames.units.tribeFullName] : "N/A");
            const finalTribes = ["N/A", ...returnedTribes];
            const unique = [...new Set(finalTribes)];
            resolve(unique);
          } else {
            resolve([""]);
          }
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          resolve([]);
        });
      } else if(field === FieldNames.featureFields.fundingTribe){
        let tribes = [];
        userRoleInfo.forEach(item => {
          //if(item.Unit === parentValue){
            if(item[FieldNames.userRoles.tribe] && tribes && !tribes.includes(item[FieldNames.userRoles.tribe])){
              tribes.push(item[FieldNames.userRoles.tribe]);
            }
          //}
        })
        //We need to use the tribe the user has assigned if they have one
        if(tribes && tribes.length > 0){
          resolve(tribes);
        } else {
          //When the user doesn't have a tribe assigned use the other fields to populate this if they exist
          if(parentType === FieldNames.featureFields.fundingRegion){
            UserHelperService.getTribesByRegions([parentValue]).then((resp) => {
              if(resp && resp.length > 0){
                const returnedTribes = resp.map(x => x[FieldNames.units.tribeFullName] ? x[FieldNames.units.tribeFullName] : "");
                resolve(returnedTribes);
              } else {
                resolve([""]);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          } else if(parentType === FieldNames.featureFields.fundingUnit){
            UserHelperService.getTribesByUnits([parentValue]).then((resp) => {
              if(resp && resp.length > 0){
                const returnedTribes = resp.map(x => x[FieldNames.units.tribeFullName] ? x[FieldNames.units.tribeFullName] : "");
                resolve(returnedTribes);
              } else {
                resolve([""]);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          } else {
            UserHelperService.getAllTribes().then((resp) => {
              if(resp && resp.length > 0){
                const returnedTribes = resp.map(x => x[FieldNames.units.tribeFullName] ? x[FieldNames.units.tribeFullName] : "");
                resolve(returnedTribes);
              } else {
                resolve([""]);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          }
        }
      } else if(field === FieldNames.featureFields.region){
        if(detail && detail.attributes && detail.attributes[FieldNames.featureFields.agency])
        {
          UserHelperService.getRegionsByAgency(detail.attributes[FieldNames.featureFields.agency]).then((resp) => {
            if(resp && resp.length > 0 && detail && detail.attributes){
              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let returnedRegions = resp.map(x => x[FieldNames.featureFields.region]);
              let finalRegions = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              finalRegions = [...finalRegions, ...returnedRegions];
              let unique = [...new Set(finalRegions)];
              resolve(unique);
            } else {
              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let finalRegions = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              resolve(finalRegions);
            }
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            resolve([]);
          });
        }
        else
        {
          UserHelperService.getAllRegions().then((resp) => {
            if(resp && resp.length > 0 && detail && detail.attributes){
              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let returnedRegions = resp.map(x => x[FieldNames.featureFields.region]);
              let finalRegions = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              finalRegions = [...finalRegions, ...returnedRegions];
              let unique = [...new Set(finalRegions)];
              resolve(unique);
            } else {
              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let finalRegions = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              resolve(finalRegions);
            }
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            resolve([]);
          });
        }
      } else if(field === FieldNames.featureFields.unit){
        // detail.attributes[FieldNames.featureFields.agency] is expected to exist from parent method call
        var currentAgency = (detail && detail.attributes && detail.attributes[FieldNames.featureFields.agency]) ? detail.attributes[FieldNames.featureFields.agency] : null;
        if(currentAgency && currentAgency.includes(FieldNames.agencies.blm))
        {
          UserHelperService.getBlmOtherUnits().then((oresp) => {
            UserHelperService.getBlmFieldUnits().then((fresp) => {
              let otherUnits = (oresp && oresp.length > 0) ? oresp.map(x => x.ADMU_NAME) : [];
              let fieldUnits = (fresp && fresp.length > 0) ? fresp.map(x => x.ADMU_NAME) : [];

              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              finalUnits = [...finalUnits, ...otherUnits, ...fieldUnits];
              let unique = [...new Set(finalUnits)];
              resolve(unique);
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            resolve([]);
          });
        }
        else if(currentAgency && currentAgency.includes(FieldNames.agencies.bia))
        {
          UserHelperService.getBiaUnits().then((resp) => {
            if(resp && resp.length > 0 && detail && detail.attributes){
              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let returnedUnits = resp.map(x => x.biaagency);
              let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              finalUnits = [...finalUnits, ...returnedUnits];
              let unique = [...new Set(finalUnits)];
              resolve(unique);
            } else {
              if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              resolve(finalUnits);
            }
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            resolve([]);
          });
        }
        else
        {
          var isMatch = false;
          for (var key of Object.keys(FieldNames.agencies)) {
            if(detail && detail.attributes && detail.attributes[FieldNames.featureFields.agency] &&
              detail.attributes[FieldNames.featureFields.agency].includes(FieldNames.agencies[key]))
            {
              isMatch = true;
            }
          }
          if(isMatch)
          {
            if(detail && detail.attributes && detail.attributes[FieldNames.featureFields.region] && detail.attributes[FieldNames.featureFields.region] !== "N/A")
            {
              const regionArr = [detail.attributes[FieldNames.featureFields.region]];
              UserHelperService.getUnitsByRegionsAgency(regionArr, currentAgency).then((resp) => {
                if(resp && resp.length > 0 && detail && detail.attributes){
                  if(detail.attributes[field])
                  {
                    setSelectionValue(detail.attributes[field]);
                  }
                  const returnedUnits = resp.map(x => x.Unit);
                  let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
                  finalUnits = [...finalUnits, ...returnedUnits];
                  let unique = [...new Set(finalUnits)];
                  resolve(unique);            
                } else {
                  if(detail.attributes[field])
                  {
                    setSelectionValue(detail.attributes[field]);
                  }
                  let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
                  resolve(finalUnits);
                }
              }).catch(error => {
                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                resolve([]);
              });
            }
            else
            {
              UserHelperService.getUnitsByAgency(currentAgency).then((resp) => {
                if(resp && resp.length > 0 && detail && detail.attributes){
                  if(detail.attributes[field])
                  {
                    setSelectionValue(detail.attributes[field]);
                  }
                  const returnedUnits = resp.map(x => x.Unit);
                  let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
                  finalUnits = [...finalUnits, ...returnedUnits];
                  let unique = [...new Set(finalUnits)];
                  resolve(unique);            
                } else {
                  if(detail.attributes[field])
                  {
                    setSelectionValue(detail.attributes[field]);
                  }
                  let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
                  resolve(finalUnits);
                }
              }).catch(error => {
                ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                resolve([]);
              });
            }
          }
          else
          {
            UserHelperService.getAllUnits().then((resp) => {
              if(resp && resp.length > 0 && detail && detail.attributes){
                if(detail.attributes[field])
                {
                  setSelectionValue(detail.attributes[field]);
                }
                const returnedUnits = resp.map(x => x.Unit);
                let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                              detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
                finalUnits = [...finalUnits, ...returnedUnits];
                let unique = [...new Set(finalUnits)];
                resolve(unique);
              } else {
                if(detail.attributes[field])
              {
                setSelectionValue(detail.attributes[field]);
              }
              let finalUnits = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                    detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
              resolve(finalUnits);
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
              resolve([]);
            });
          }
        }
      } else if(field === FieldNames.featureFields.projectName){
        ProjectService.getAllProjects().then((resp) => {
          if(resp && resp.length > 0 && detail && detail.attributes){
            let detailItem = detail?.attributes[FieldNames.featureFields.projectName];
            setSelectionValue(detailItem);
            const projectsWithNames = resp.filter(x => x[FieldNames.projectFields.name]);
            const returnedProjects = projectsWithNames.map(x => 
            {
              let mappedItem = getProjectNameDateIdentifier(x, true);
              return mappedItem;
            });
            setAllProjects(projectsWithNames);
            let finalProjects = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
            detailItem ? [detailItem,"N/A"] : ["N/A"];
                          finalProjects = [...finalProjects, ...returnedProjects];
            let unique = [...new Set(finalProjects)];
            resolve(unique);
          } else {
            if(detail.attributes[field])
            {
              setSelectionValue(detail.attributes[field]);
            }
            let finalProjects = savedSelectionValue ? [savedSelectionValue, "N/A"] : 
                                  detail.attributes[field] ? [detail.attributes[field],"N/A"] : ["N/A"];
            resolve(finalProjects);
          }
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          resolve([]);
        });
      }
    })
  }

  return(
    <FormControl variant="standard" fullWidth>
      <ThemeProvider theme={theme}>
      <Autocomplete
        id={`user${field}Demo`}
        value={selectionValue ? selectionValue : ""}
        onChange={(event, newValue) => {
          if(field === FieldNames.featureFields.projectName){
            const indProject = allProjects?.filter(x => 
            {
              let mappedItem = getProjectNameDateIdentifier(x, true);
              return mappedItem === newValue;
            })
            let projectname = "";
            if(indProject?.length > 0){
              projectname = indProject[0]?.[FieldNames.projectFields.name];
              onFieldChange(FieldNames.featureFields.projectID, indProject[0][FieldNames.projectFields.id], indProject[0]);
            } else {
              onFieldChange(FieldNames.featureFields.projectID, null)
            }
            setSelectionValue(projectname);
          } else {
            setSelectionValue(newValue);
            onFieldChange(field, newValue);
          }
        }}
        onOpen={() => {
          //setMenuOpen(true);
          setMenuLoading(true);
          getMenuOptions().then((allOptions) => {
            setOptions(allOptions);
            setMenuLoading(false)
          })
        }}
        onClose={() => {
          //setMenuOpen(false);
        }}
        isOptionEqualToValue={(option, value) => {
          if(value === ""){
            return true;
          } else {
            return option === value;
          }
        }}
        getOptionLabel={(option) => { 
          return option 
        }}
        options={options}
        loading={menuLoading}
        renderInput={(params) => (
          <TextField
            variant='standard'
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                classes: {...(isRequired && inputValueInvalid(field)) ? classes : showNotSavedClasses(field) ? notsavedclasses : ""},
                endAdornment: (
                  <React.Fragment>
                    {menuLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params?.InputProps?.endAdornment}
                  </React.Fragment>
                )
              }
            }}
          />
        )}
      />
      </ThemeProvider>
    </FormControl>
  ) 
}